import { useEffect, useMemo, useState } from "react";
import {
  Archive,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  Files,
  LoaderCircle,
  Search,
  Scale,
  Sparkles,
} from "lucide-react";
import { encodeCuijPath } from "@tribunalea/shared";
import { useApi } from "../App.jsx";
import {
  DataTable,
  DataTableAction,
  DataTableBadge,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTablePrimaryCell,
  DataTableRow,
  DataTableStatus,
  DataTableValue,
  CopyableDate,
  EmptyState,
  PageHeader,
  useRouter,
} from "@tribunalea/ui";
import { compareExpedienteCards } from "../lib/expediente-card-order.js";
import { buildExpedientesReport } from "../lib/expedientes-report.js";
import { trafficColorToTone } from "../lib/expediente-traffic-light.js";
import {
  expedienteImportantItems,
  importantItemDateLabel,
  importantItemReviewAction,
  importantItemStatusLabel,
  importantItemTone,
} from "../lib/expediente-important-items.js";

const dateOnlyFormatter = new Intl.DateTimeFormat("es-AR", {
  dateStyle: "medium",
});

function formatActivityDate(value) {
  if (!value) return "Sin actividad";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Sin fecha"
    : dateOnlyFormatter.format(date);
}

function parseDateOnly(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) return null;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day), 12);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateOnly(value) {
  const date = parseDateOnly(value);
  return date ? dateOnlyFormatter.format(date) : "Fecha por confirmar";
}

function attentionView(expediente) {
  const reminder = expediente.procedureState?.nextReminder;
  const tone = expediente.trafficColor
    ? trafficColorToTone(expediente.trafficColor)
    : "neutral";
  const labels = {
    danger: "Requiere atención",
    warning: "Próximo",
    success: "Al día",
    neutral: "Sin evaluar",
  };

  let description = expediente.procedureState?.hasEvaluation
    ? "Sin pendientes próximos"
    : "Pendiente de análisis";

  if (reminder) {
    description = reminder.dueDate ? (
      <>
        {reminder.label} ·{" "}
        <CopyableDate value={reminder.dueDate}>
          {formatDateOnly(reminder.dueDate)}
        </CopyableDate>
      </>
    ) : reminder.label;
  }

  return {
    tone,
    label: labels[tone] || labels.neutral,
    description,
  };
}

function ExpedientesTable({ expedientes, showArchived, onOpen }) {
  return (
    <DataTable
      ariaLabel={showArchived ? "Expedientes archivados" : "Mis expedientes"}
      className="expedientes-table"
      minWidth="560px"
    >
      <DataTableHead>
        <DataTableRow>
          <DataTableHeaderCell className="expedientes-table-case">
            Expediente
          </DataTableHeaderCell>
          <DataTableHeaderCell
            className="expedientes-table-activity"
            priority="secondary"
          >
            Última actividad
          </DataTableHeaderCell>
          <DataTableHeaderCell className="expedientes-table-attention">
            Estado
          </DataTableHeaderCell>
          <DataTableHeaderCell
            className="expedientes-table-action"
            align="end"
          >
            <span className="sr-only">Acciones</span>
          </DataTableHeaderCell>
        </DataTableRow>
      </DataTableHead>
      <DataTableBody>
        {expedientes.map((expediente) => {
          const expedienteNumber = String(expediente.numero ?? "").trim();
          const attention = attentionView(expediente);
          const open = () => onOpen(expediente);
          return (
            <DataTableRow
              key={expediente.savedId || expediente.cuij}
              label={`Abrir ${expediente.cuij}`}
              onActivate={open}
            >
              <DataTableCell className="expedientes-table-case">
                <DataTablePrimaryCell
                  eyebrow={`${expediente.cuij}${expedienteNumber
                    ? ` · N.º ${expedienteNumber}`
                    : ""}`}
                  title={expediente.caratula || "Sin carátula informada"}
                  badges={(
                    <>
                      {expediente.procedureState?.hasEvaluation && (
                        <DataTableBadge
                          tone="ai"
                          icon={<Sparkles size={9} aria-hidden="true" />}
                        >
                          IA
                        </DataTableBadge>
                      )}
                      {showArchived && (
                        <DataTableBadge
                          tone="warning"
                          icon={<Archive size={9} aria-hidden="true" />}
                        >
                          Archivado
                        </DataTableBadge>
                      )}
                    </>
                  )}
                />
              </DataTableCell>
              <DataTableCell priority="secondary">
                <DataTableValue
                  primary={(
                    <CopyableDate value={expediente.lastActivityAt}>
                      {formatActivityDate(expediente.lastActivityAt)}
                    </CopyableDate>
                  )}
                />
              </DataTableCell>
              <DataTableCell>
                <DataTableStatus {...attention} />
              </DataTableCell>
              <DataTableCell align="end">
                <DataTableAction
                  label={`Abrir expediente ${expediente.cuij}`}
                  icon={<ChevronRight size={16} aria-hidden="true" />}
                  onClick={open}
                />
              </DataTableCell>
            </DataTableRow>
          );
        })}
      </DataTableBody>
    </DataTable>
  );
}

function ExpedientesPortfolioStatus({ expedientes, showArchived }) {
  const report = useMemo(
    () => buildExpedientesReport(expedientes),
    [expedientes],
  );
  const chartLabel = report.states
    .map(({ count, label }) => `${label}: ${count}`)
    .join(", ");

  return (
    <section
      aria-label={`Estado de la cartera. ${report.total} expedientes. ${chartLabel}`}
      className="expedientes-portfolio-status"
    >
      <div
        aria-hidden="true"
        className="expedientes-portfolio-donut"
        style={{ background: report.gradient }}
      >
        <div>
          <strong>{report.total}</strong>
          <span>Total</span>
        </div>
      </div>
      <div className="expedientes-portfolio-copy">
        <header>
          <strong>Estado de la cartera</strong>
          <span>{showArchived ? "Archivados" : "Activos"}</span>
        </header>
        <ul>
          {report.states.map((state) => (
            <li
              className={`is-${state.key}`}
              key={state.key}
              title={`${state.label}: ${state.count} (${state.percentage}%)`}
            >
              <span aria-hidden="true" />
              <small>{state.label}</small>
              <strong>{state.count}</strong>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function ExpedientesImportantSection({
  actionError,
  actionSaving,
  expedientes,
  onMarkReviewed,
  onOpen,
}) {
  const items = useMemo(
    () => expedienteImportantItems(expedientes),
    [expedientes],
  );
  const redCount = items.filter(
    ({ trafficColor }) => trafficColor === "red",
  ).length;
  const yellowCount = items.length - redCount;

  return (
    <section
      aria-labelledby="expedientes-important-title"
      className="expedientes-important"
    >
      <header>
        <span className="expedientes-important-icon">
          <CalendarClock size={18} aria-hidden="true" />
        </span>
        <div>
          <small>ROJOS Y AMARILLOS</small>
          <h2 id="expedientes-important-title">Recordatorios importantes</h2>
        </div>
        <span className="expedientes-important-count">
          {items.length
            ? `${redCount} ${redCount === 1 ? "rojo" : "rojos"} · ${yellowCount} ${yellowCount === 1 ? "amarillo" : "amarillos"}`
            : "Sin pendientes"}
        </span>
      </header>

      {items.length ? (
        <div className="expedientes-important-list">
          {actionError && (
            <div className="inline-error expedientes-important-error" role="alert">
              {actionError}
            </div>
          )}
          <DataTable
            ariaLabel="Recordatorios importantes de expedientes activos"
            className="expedientes-important-table"
            density="compact"
            minWidth="720px"
          >
            <DataTableHead>
              <DataTableRow>
                <DataTableHeaderCell className="expedientes-important-detail">
                  Importante
                </DataTableHeaderCell>
                <DataTableHeaderCell className="expedientes-important-case">
                  Expediente
                </DataTableHeaderCell>
                <DataTableHeaderCell className="expedientes-important-date">
                  Fecha
                </DataTableHeaderCell>
                <DataTableHeaderCell
                  className="expedientes-important-action"
                  align="end"
                >
                  <span className="sr-only">Acciones</span>
                </DataTableHeaderCell>
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {items.map((item) => {
                const tone = importantItemTone(item);
                const open = () => onOpen(item);
                const caseNumber = String(
                  item.expediente.numero ?? "",
                ).trim();
                const dateLabel = importantItemDateLabel(item);
                const reviewAction = importantItemReviewAction(item);
                const saving = actionSaving ===
                  `${item.expediente.cuij}:${item.id}`;
                return (
                  <DataTableRow
                    className={`is-important-${tone}`}
                    key={`${item.expediente.cuij}:${item.id}`}
                  >
                    <DataTableCell className="expedientes-important-detail">
                      <DataTablePrimaryCell
                        icon={item.type === "inconsistency"
                          ? <CircleAlert size={15} aria-hidden="true" />
                          : <CalendarClock size={15} aria-hidden="true" />}
                        title={item.label}
                        meta={item.description || item.duePartyLabel || null}
                        badges={(
                          <DataTableBadge tone={tone}>
                            {importantItemStatusLabel(item)}
                          </DataTableBadge>
                        )}
                      />
                    </DataTableCell>
                    <DataTableCell className="expedientes-important-case">
                      <DataTableValue
                        primary={`${item.expediente.cuij}${caseNumber
                          ? ` · N.º ${caseNumber}`
                          : ""}`}
                        secondary={
                          item.expediente.caratula || "Sin carátula informada"
                        }
                      />
                    </DataTableCell>
                    <DataTableCell className="expedientes-important-date">
                      <DataTableValue
                        primary={item.dueDate
                          ? formatDateOnly(item.dueDate)
                          : dateLabel}
                        secondary={item.dueDate ? dateLabel : null}
                      />
                    </DataTableCell>
                    <DataTableCell
                      className="expedientes-important-action"
                      align="end"
                    >
                      <div className="expedientes-important-actions">
                        {reviewAction && (
                          <DataTableAction
                            disabled={Boolean(actionSaving)}
                            label={`${reviewAction.ariaLabel}: ${item.label}`}
                            icon={saving
                              ? <LoaderCircle className="spin" size={14} />
                              : <Check size={14} aria-hidden="true" />}
                            onClick={() => onMarkReviewed(item)}
                          >
                            {reviewAction.label}
                          </DataTableAction>
                        )}
                        <DataTableAction
                          disabled={Boolean(actionSaving)}
                          label={`Abrir ${item.expediente.cuij}`}
                          icon={<ChevronRight size={16} aria-hidden="true" />}
                          onClick={open}
                        >
                          Abrir
                        </DataTableAction>
                      </div>
                    </DataTableCell>
                  </DataTableRow>
                );
              })}
            </DataTableBody>
          </DataTable>
        </div>
      ) : (
        <div className="expedientes-important-empty">
          <CheckCircle2 size={18} aria-hidden="true" />
          <span>No hay recordatorios importantes en tus expedientes activos.</span>
        </div>
      )}
    </section>
  );
}

export function ExpedientesPage() {
  const api = useApi();
  const { navigate } = useRouter();
  const [expedientes, setExpedientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [importantActionError, setImportantActionError] = useState("");
  const [importantActionSaving, setImportantActionSaving] = useState("");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [fueroFilter, setFueroFilter] = useState("");

  const load = async () => {
    try {
      setError("");
      setExpedientes(
        await api.listExpedientes({ archived: showArchived }),
      );
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  const markImportantItemReviewed = async (item) => {
    const actionKey = `${item.expediente.cuij}:${item.id}`;
    try {
      setImportantActionError("");
      setImportantActionSaving(actionKey);
      if (item.type === "inconsistency") {
        await api.setExpedienteInconsistencyAcknowledgment(
          item.expediente.cuij,
          String(item.id).replace(/^inconsistency:/, ""),
          true,
        );
      } else if (item.type === "deadline") {
        await api.setExpedienteDeadlineOverride(
          item.expediente.cuij,
          item.id,
          "completed",
        );
      }
      await load();
    } catch (requestError) {
      setImportantActionError(requestError.message);
    } finally {
      setImportantActionSaving("");
    }
  };

  useEffect(() => {
    setFueroFilter("");
    setLoading(true);
    load();
  }, [showArchived]);

  const availableFueros = useMemo(
    () =>
      Array.from(
        new Set(
          expedientes
            .map(({ fuero }) => String(fuero || "").trim())
            .filter(Boolean),
        ),
      ).sort((left, right) =>
        left.localeCompare(right, "es", { sensitivity: "base" }),
      ),
    [expedientes],
  );

  const visibleExpedientes = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("es");
    return expedientes
      .filter((expediente) => {
        if (fueroFilter && expediente.fuero !== fueroFilter) return false;
        if (!term) return true;
        return [
          expediente.cuij,
          expediente.numero,
          expediente.caratula,
          expediente.organismoActual,
          expediente.fuero,
        ]
          .filter((value) => value !== undefined && value !== null)
          .some((value) =>
            String(value).toLocaleLowerCase("es").includes(term),
          );
      })
      .sort(compareExpedienteCards);
  }, [expedientes, fueroFilter, search]);

  return (
    <div className="page expedientes-page">
      <PageHeader
        actions={!loading && expedientes.length > 0 ? (
          <ExpedientesPortfolioStatus
            expedientes={expedientes}
            showArchived={showArchived}
          />
        ) : null}
        className="expedientes-heading"
        eyebrow="Praxis"
        title="Mis expedientes"
        description="Revisá y organizá los expedientes que agregaste a tu biblioteca."
      />

      {!loading && !showArchived && expedientes.length > 0 && (
        <ExpedientesImportantSection
          actionError={importantActionError}
          actionSaving={importantActionSaving}
          expedientes={expedientes}
          onMarkReviewed={markImportantItemReviewed}
          onOpen={(item) => {
            const target = `/expedientes/${encodeCuijPath(
              item.expediente.cuij,
            )}`;
            navigate(
              item.type === "inconsistency"
                ? target
                : item.type === "deadline"
                  ? `${target}/recordatorios?recordatorio=${encodeURIComponent(
                      item.id,
                    )}`
                  : `${target}/recordatorios`,
            );
          }}
        />
      )}

      <div className="expedientes-section-heading">
        <div>
          <h2>{showArchived ? "Expedientes archivados" : "Mis expedientes"}</h2>
          <span>
            {expedientes.length}{" "}
            {expedientes.length === 1 ? "expediente" : "expedientes"}
          </span>
        </div>
        <div className="expedientes-list-tools">
          <div className="expedientes-view-toggle" role="group" aria-label="Vista de expedientes">
            <button
              className={!showArchived ? "active" : ""}
              type="button"
              onClick={() => setShowArchived(false)}
            >
              Activos
            </button>
            <button
              className={showArchived ? "active" : ""}
              type="button"
              onClick={() => setShowArchived(true)}
            >
              Archivados
            </button>
          </div>
          <label className="expedientes-fuero-filter">
            <Scale size={16} />
            <select
              value={fueroFilter}
              onChange={(event) => setFueroFilter(event.target.value)}
              aria-label="Filtrar expedientes por fuero"
            >
              <option value="">Todos los fueros</option>
              {availableFueros.map((fuero) => (
                <option key={fuero} value={fuero}>
                  {fuero}
                </option>
              ))}
            </select>
          </label>
          <label className="search-field expedientes-search">
            <Search size={17} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filtrar Mis expedientes"
              aria-label="Filtrar Mis expedientes"
            />
          </label>
        </div>
      </div>

      {error && (
        <div className="inline-error">
          <span>{error}</span>
          <button onClick={load}>Reintentar</button>
        </div>
      )}

      {loading ? (
        <div className="loading-list">
          <span />
          <span />
        </div>
      ) : visibleExpedientes.length ? (
        <ExpedientesTable
          expedientes={visibleExpedientes}
          showArchived={showArchived}
          onOpen={(expediente) =>
            navigate(`/expedientes/${encodeCuijPath(expediente.cuij)}`)
          }
        />
      ) : expedientes.length ? (
        <div className="no-results expedientes-no-results">
          <Search size={22} />
          <h2>No encontramos expedientes</h2>
          <p>Probá buscando por otro CUIJ, número, carátula u organismo.</p>
        </div>
      ) : (
        <EmptyState
          className="expedientes-empty"
          icon={Files}
          title={showArchived
            ? "No hay expedientes archivados"
            : "Todavía no agregaste expedientes"}
          description={showArchived
            ? "Los expedientes que archives aparecerán acá y podrás reactivarlos cuando quieras."
            : "Buscá un expediente y agregalo a tu biblioteca para verlo acá."}
          action={!showArchived ? (
            <button
              className="button primary"
              type="button"
              onClick={() => navigate("/buscar-expedientes")}
            >
              <Search size={17} />
              Buscar expedientes
            </button>
          ) : null}
        />
      )}
    </div>
  );
}
